import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { db, isConfigured } from '../firebaseConfig';
import { collection, doc, getDoc, setDoc, getDocs, query, where } from 'firebase/firestore';

export default function Profile() {
  const [profile, setProfile] = useState({
    name: '',
    bio: '',
    profileImage: '',
    cvUrl: '',
    email: '',
    phone: '',
    linkedin: '',
    github: '',
    figma: '',
    likes: ['Coca-Cola', 'Coffee', 'Baby-Pink', 'teddy-bears'],
    techStack: [
      { name: 'HTML', imageUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTBd2rMcfI56SZEenhvautbZl_xUsPSHaktNw&s' },
      { name: 'CSS', imageUrl: 'https://cdn-icons-png.flaticon.com/512/5968/5968242.png' },
      { name: 'JavaScript', imageUrl: 'https://img.icons8.com/color/512/javascript.png' }
    ],
    startYear: 2021
  });
  
  const [stats, setStats] = useState({
    completedProjects: 0,
    inProgressProjects: 0,
    yearsOfExperience: 0
  });

  const [newLike, setNewLike] = useState('');
  const [newTech, setNewTech] = useState({ name: '', imageUrl: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isConfigured) {
      fetchProfile();
      fetchStats();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchProfile = async () => {
    if (!isConfigured) return;
    try {
      const docRef = doc(db, 'profile', 'main');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setProfile(docSnap.data());
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!isConfigured) return;
    try {
      // Count completed projects
      const completedQuery = query(
        collection(db, 'projects'),
        where('status', '==', 'Completed')
      );
      const completedSnapshot = await getDocs(completedQuery);
      
      // Count in-progress projects
      const inProgressQuery = query(
        collection(db, 'projects'),
        where('status', '==', 'In Progress')
      );
      const inProgressSnapshot = await getDocs(inProgressQuery);
      
      // Calculate years of experience
      const currentYear = new Date().getFullYear();
      const yearsOfExperience = currentYear - (profile.startYear || 2021);
      
      setStats({
        completedProjects: completedSnapshot.size,
        inProgressProjects: inProgressSnapshot.size,
        yearsOfExperience
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!isConfigured) {
      alert('Firebase is not configured.');
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, 'profile', 'main'), {
        ...profile,
        updatedAt: new Date().toISOString()
      });
      alert('Profile saved successfully!');
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addLike = () => {
    if (newLike.trim()) {
      setProfile(prev => ({
        ...prev,
        likes: [...prev.likes, newLike.trim()]
      }));
      setNewLike('');
    }
  };

  const removeLike = (index) => {
    setProfile(prev => ({
      ...prev,
      likes: prev.likes.filter((_, i) => i !== index)
    }));
  };

  const addTech = () => {
    if (newTech.name.trim() && newTech.imageUrl.trim()) {
      setProfile(prev => ({
        ...prev,
        techStack: [...prev.techStack, { ...newTech }]
      }));
      setNewTech({ name: '', imageUrl: '' });
    }
  };

  const removeTech = (index) => {
    setProfile(prev => ({
      ...prev,
      techStack: prev.techStack.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className='flex min-h-screen bg-[#FFB7CE] items-center justify-center'>
        <div className='text-white text-xl'>Loading profile...</div>
      </div>
    );
  }

  return (
    <div className='flex min-h-screen bg-[#FFB7CE]'>
      <Sidebar />
      <div className='flex-1 p-4 pt-20 lg:pt-4 lg:pl-8'>
        <div className='max-w-6xl mx-auto'>
          {!isConfigured && (
            <div className='bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded'>
              <p className='font-bold'>⚠️ Firebase Not Configured</p>
            </div>
          )}

          {/* Header */}
          <div className='flex justify-between items-center mb-6'>
            <div>
              <h1 className='text-3xl font-bold text-white'>PROFILE</h1>
              <p className='text-white text-sm mt-1'>
                Manage your public profile information
              </p>
            </div>
            <button
              onClick={handleSave}
              disabled={saving || !isConfigured}
              className='bg-[#89CFF0] hover:bg-[#6BB8E0] text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 disabled:bg-gray-300'
            >
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>

          {/* Stats Cards */}
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
            <div className='bg-white rounded-lg p-6 shadow-lg'>
              <h3 className='text-gray-600 text-sm font-medium mb-2'>Years of Experience</h3>
              <p className='text-4xl font-bold text-[#FFB7CE]'>{stats.yearsOfExperience}+</p>
              <p className='text-xs text-gray-500 mt-2'>Since {profile.startYear}</p>
            </div>
            <div className='bg-white rounded-lg p-6 shadow-lg'>
              <h3 className='text-gray-600 text-sm font-medium mb-2'>Completed Projects</h3>
              <p className='text-4xl font-bold text-green-500'>{stats.completedProjects}</p>
              <p className='text-xs text-gray-500 mt-2'>Auto-counted from projects</p>
            </div>
            <div className='bg-white rounded-lg p-6 shadow-lg'>
              <h3 className='text-gray-600 text-sm font-medium mb-2'>In Progress</h3>
              <p className='text-4xl font-bold text-yellow-500'>{stats.inProgressProjects}</p>
              <p className='text-xs text-gray-500 mt-2'>Currently working on</p>
            </div>
          </div>

          {/* Basic Information */}
          <div className='bg-white rounded-lg p-6 mb-6 shadow-lg'>
            <h2 className='text-xl font-bold text-gray-800 mb-4'>Basic Information</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-gray-700 font-medium mb-2'>Full Name</label>
                <input
                  type='text'
                  name='name'
                  value={profile.name}
                  onChange={handleInputChange}
                  placeholder='Drusilla Khulwael'
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
                />
                <p className='text-xs text-gray-500 mt-1'>Used in: About section</p>
              </div>

              <div>
                <label className='block text-gray-700 font-medium mb-2'>Start Year</label>
                <input
                  type='number'
                  name='startYear'
                  value={profile.startYear}
                  onChange={handleInputChange}
                  placeholder='2021'
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
                />
                <p className='text-xs text-gray-500 mt-1'>For calculating years of experience</p>
              </div>

              <div className='md:col-span-2'>
                <label className='block text-gray-700 font-medium mb-2'>Bio</label>
                <textarea
                  name='bio'
                  value={profile.bio}
                  onChange={handleInputChange}
                  rows='4'
                  placeholder="I'm passionate about building modern and scalable software solutions..."
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
                />
                <p className='text-xs text-gray-500 mt-1'>Used in: About section</p>
              </div>

              <div>
                <label className='block text-gray-700 font-medium mb-2'>Profile Image URL</label>
                <input
                  type='url'
                  name='profileImage'
                  value={profile.profileImage}
                  onChange={handleInputChange}
                  placeholder='https://example.com/image.jpg'
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
                />
                <p className='text-xs text-gray-500 mt-1'>Used in: About section image</p>
              </div>

              <div>
                <label className='block text-gray-700 font-medium mb-2'>CV/Resume URL</label>
                <input
                  type='url'
                  name='cvUrl'
                  value={profile.cvUrl}
                  onChange={handleInputChange}
                  placeholder='https://example.com/resume.pdf'
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
                />
                <p className='text-xs text-gray-500 mt-1'>Used in: About section download button</p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className='bg-white rounded-lg p-6 mb-6 shadow-lg'>
            <h2 className='text-xl font-bold text-gray-800 mb-4'>Contact & Socials</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-gray-700 font-medium mb-2'>Email</label>
                <input
                  type='email'
                  name='email'
                  value={profile.email}
                  onChange={handleInputChange}
                  placeholder='your@email.com'
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
                />
              </div>

              <div>
                <label className='block text-gray-700 font-medium mb-2'>Phone</label>
                <input
                  type='tel'
                  name='phone'
                  value={profile.phone}
                  onChange={handleInputChange}
                  placeholder='+254701780172'
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
                />
              </div>

              <div>
                <label className='block text-gray-700 font-medium mb-2'>LinkedIn URL</label>
                <input
                  type='url'
                  name='linkedin'
                  value={profile.linkedin}
                  onChange={handleInputChange}
                  placeholder='https://linkedin.com/in/username'
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
                />
              </div>

              <div>
                <label className='block text-gray-700 font-medium mb-2'>GitHub URL</label>
                <input
                  type='url'
                  name='github'
                  value={profile.github}
                  onChange={handleInputChange}
                  placeholder='https://github.com/username'
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
                />
              </div>

              <div className='md:col-span-2'>
                <label className='block text-gray-700 font-medium mb-2'>Figma URL</label>
                <input
                  type='url'
                  name='figma'
                  value={profile.figma}
                  onChange={handleInputChange}
                  placeholder='https://figma.com/@username'
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
                />
              </div>
            </div>
            <p className='text-xs text-gray-500 mt-4'>Used in: Socials component (footer icons)</p>
          </div>

          {/* Likes/Interests */}
          <div className='bg-white rounded-lg p-6 mb-6 shadow-lg'>
            <h2 className='text-xl font-bold text-gray-800 mb-4'>Likes / Interests</h2>
            <p className='text-sm text-gray-600 mb-4'>These rotate on the Home page: "I love [item]"</p>
            
            <div className='flex gap-2 mb-4'>
              <input
                type='text'
                value={newLike}
                onChange={(e) => setNewLike(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addLike()}
                placeholder='Add new like (e.g., Coffee)'
                className='flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
              />
              <button
                onClick={addLike}
                className='bg-[#89CFF0] hover:bg-[#6BB8E0] text-white px-6 py-2 rounded-lg font-medium'
              >
                Add
              </button>
            </div>

            <div className='flex flex-wrap gap-2'>
              {profile.likes.map((like, index) => (
                <div
                  key={index}
                  className='bg-[#FFB7CE] bg-opacity-20 px-4 py-2 rounded-full flex items-center gap-2'
                >
                  <span className='text-gray-700'>{like}</span>
                  <button
                    onClick={() => removeLike(index)}
                    className='text-red-500 hover:text-red-700'
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Tech Stack */}
          <div className='bg-white rounded-lg p-6 shadow-lg'>
            <h2 className='text-xl font-bold text-gray-800 mb-4'>Tech Stack</h2>
            <p className='text-sm text-gray-600 mb-4'>Technologies displayed below Services section</p>
            
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
              <input
                type='text'
                value={newTech.name}
                onChange={(e) => setNewTech(prev => ({ ...prev, name: e.target.value }))}
                placeholder='Technology name (e.g., React)'
                className='px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
              />
              <div className='flex gap-2'>
                <input
                  type='url'
                  value={newTech.imageUrl}
                  onChange={(e) => setNewTech(prev => ({ ...prev, imageUrl: e.target.value }))}
                  placeholder='Image URL'
                  className='flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE]'
                />
                <button
                  onClick={addTech}
                  className='bg-[#89CFF0] hover:bg-[#6BB8E0] text-white px-6 py-2 rounded-lg font-medium'
                >
                  Add
                </button>
              </div>
            </div>

            <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4'>
              {profile.techStack.map((tech, index) => (
                <div
                  key={index}
                  className='relative bg-gray-50 p-4 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow'
                >
                  <button
                    onClick={() => removeTech(index)}
                    className='absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600'
                  >
                    ×
                  </button>
                  <img
                    src={tech.imageUrl}
                    alt={tech.name}
                    className='w-16 h-16 mx-auto mb-2 object-contain'
                  />
                  <p className='text-center text-sm font-medium text-gray-700'>{tech.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}